import {Button, Container, Group, Stack, Stepper, Text, Title} from '@mantine/core';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Elements} from '@stripe/react-stripe-js';
import {loadStripe, StripeElementsOptions} from '@stripe/stripe-js';
import {PaymentElement, useElements, useStripe} from '@stripe/react-stripe-js';
import {CustomerSelector} from '../components/CustomerSelector';
import {FlowResultDisplay} from '../components/FlowResultDisplay';
import {FlowResult} from '../types';
import {add as createSetupIntent} from '../../setup-intents/services/SetupIntentsService';
import {SetupIntent} from '../../setup-intents/types';
import {notifyError} from '../../../components/ServiceNotifications';
import config from '../../../config';

const stripePromise = loadStripe(config.stripe.publishableKey);

const SetupForm = ({onResult}: { onResult: (result: FlowResult) => void }) => {
    const stripe = useStripe();
    const elements = useElements();

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!stripe || !elements) return;

        const {error, setupIntent} = await stripe.confirmSetup({
            elements,
            confirmParams: {
                return_url: window.location.href,
            },
            redirect: 'if_required',
        });

        if (error) {
            onResult({success: false, error: error.message});
        } else {
            onResult({
                success: true,
                intentId: setupIntent?.id,
                paymentMethodId: setupIntent?.payment_method as string,
                status: setupIntent?.status,
            });
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement/>
            <Button my="sm" type="submit" disabled={!stripe}>
                Save Payment Method
            </Button>
        </form>
    );
};

export const SavePaymentMethodPage = () => {
    const navigate = useNavigate();
    const [active, setActive] = useState(0);
    const [customerId, setCustomerId] = useState<string | null>(null);
    const [setupIntent, setSetupIntent] = useState<SetupIntent | null>(null);
    const [creating, setCreating] = useState(false);
    const [result, setResult] = useState<FlowResult | null>(null);

    const handleCreateSetupIntent = async () => {
        if (!customerId) return;
        setCreating(true);
        try {
            const si = await createSetupIntent({
                customerId,
                paymentMethodTypes: ['card'],
                usage: 'offSession',
            });
            console.log('Setup intent created:', si);
            setSetupIntent(si as SetupIntent);
            setActive(1);
        } catch (e) {
            notifyError(e);
        } finally {
            setCreating(false);
        }
    };

    const handleResult = (flowResult: FlowResult) => {
        setResult(flowResult);
        setActive(2);
    };

    const handleReset = () => {
        setActive(0);
        setCustomerId(null);
        setSetupIntent(null);
        setResult(null);
    };

    const options = setupIntent?.clientSecret ? {
        clientSecret: setupIntent.clientSecret,
        appearance: {
            theme: 'stripe',
            rules: {
                '.Input': {
                    border: '1px solid #E0E6EB',
                },
            },
        },
    } as StripeElementsOptions : undefined;

    return (
        <Container size="sm" py="xl">
            <Stack gap="xl">
                <div>
                    <Title order={3}>Save Payment Method</Title>
                    <Text c="dimmed" size="sm">
                        Create a setup intent and save a card to a Stripe customer.
                    </Text>
                </div>

                <Stepper active={active} allowNextStepsSelect={false}>
                    <Stepper.Step label="Select Customer"/>
                    <Stepper.Step label="Enter Payment Details"/>
                    <Stepper.Step label="Result"/>
                </Stepper>

                {active === 0 && (
                    <Stack gap="md">
                        <CustomerSelector value={customerId} onChange={setCustomerId}/>
                        <Group justify="space-between">
                            <Button variant="subtle" onClick={() => navigate('/payment-flow-tester')}>
                                Back
                            </Button>
                            <Button
                                onClick={handleCreateSetupIntent}
                                disabled={!customerId}
                                loading={creating}
                            >
                                Create Setup Intent
                            </Button>
                        </Group>
                    </Stack>
                )}

                {active === 1 && options && (
                    <Elements stripe={stripePromise} options={options}>
                        <SetupForm onResult={handleResult}/>
                    </Elements>
                )}

                {active === 2 && result && (
                    <FlowResultDisplay result={result} onReset={handleReset}/>
                )}
            </Stack>
        </Container>
    );
};
